import React, { useState, useEffect, useRef } from 'react';
import {
  Store, ShoppingBag, DollarSign, Loader2, RefreshCw, AlertTriangle, XCircle, Clock, Volume2, VolumeX,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pop-Up Shop Sales widget with full payment-status breakdown.
 *
 * Phase 3 (Feb 2026): replaces the simple today+week stats with the
 * paid/pending/failed/refunded/expired breakdown driven by
 * /api/admin/payments/breakdown?source=pop_up_shop.
 *
 * Phase 4: auto-refresh every 3s when the "Live Pulse" toggle is on, plus an
 * audible beep when failed-payment count rises during a live event.
 */
const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatTile = ({ icon: Icon, label, count, amount, tone = 'gray', testId }) => {
  const tones = {
    green: 'border-green-200 bg-green-50',
    red: 'border-red-200 bg-red-50',
    amber: 'border-amber-200 bg-amber-50',
    gray: 'border-[#E5E7EB] bg-white',
  };
  const iconTones = { green: 'text-green-700', red: 'text-red-700', amber: 'text-amber-700', gray: 'text-[#4B5563]' };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconTones[tone]}`} />
        <p className="text-[10px] uppercase tracking-wide font-semibold text-[#4B5563]">{label}</p>
      </div>
      <div className="font-heading text-lg font-bold text-[#0A0A0A]">{count}</div>
      {amount !== undefined && <p className="text-[11px] text-[#4B5563] font-mono">{fmt(amount)}</p>}
    </div>
  );
};

const PopupShopSalesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livePulse, setLivePulse] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const failedCountRef = useRef(0);
  const pollRef = useRef(null);

  const fetchSales = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/payments/breakdown?source=pop_up_shop`, { withCredentials: true });
      const next = r.data;
      // Audible alert when a NEW failed payment lands
      if (soundOn && data && next.failed.count > failedCountRef.current) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.value = 440; g.gain.value = 0.1;
          o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 350);
        } catch {}
      }
      failedCountRef.current = next.failed.count;
      setData(next);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSales(); }, []);

  useEffect(() => {
    if (livePulse) {
      pollRef.current = setInterval(() => fetchSales(true), 3000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [livePulse, soundOn]); // eslint-disable-line

  const hasIssues = data && (data.failed.count > 0 || data.pending.count > 0);

  return (
    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4" data-testid="popup-shop-sales-widget">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-[#BA1826]" />
          <div>
            <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Pop-Up Shop QR Sales</h3>
            <p className="text-[10px] text-[#4B5563]">Sales tracked from the permanent pop-up shop QR code. Only paid/refunded statuses count toward revenue.</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setSoundOn(s => !s)} title={soundOn ? 'Mute alerts' : 'Enable sound alerts'}
            className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1 ${soundOn ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-[#FECACA] bg-white text-[#4B5563]'}`}
            data-testid="popup-sales-sound-toggle">
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          </button>
          <label className="text-[11px] px-2 py-1 rounded border border-[#FECACA] bg-white text-[#BA1826] flex items-center gap-1 cursor-pointer" data-testid="popup-sales-live-pulse-toggle">
            <input type="checkbox" checked={livePulse} onChange={(e) => setLivePulse(e.target.checked)} className="accent-[#BA1826] w-3 h-3" />
            Live Pulse {livePulse && <span className="w-1.5 h-1.5 rounded-full bg-[#BA1826] animate-pulse" />}
          </label>
          <button onClick={() => fetchSales(true)} disabled={loading}
            className="text-[11px] px-2 py-1 rounded border border-[#FECACA] bg-white text-[#BA1826] hover:bg-[#FEF2F2] flex items-center gap-1 disabled:opacity-50"
            data-testid="popup-sales-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {hasIssues && (
        <div className="bg-amber-100 border border-amber-300 text-amber-900 text-xs px-3 py-2 rounded mb-3 flex items-center gap-2" data-testid="popup-sales-issues-alert">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span><strong>{data.failed.count}</strong> failed / <strong>{data.pending.count}</strong> pending payments. Do not release items for unpaid orders.</span>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-[#BA1826]" />
        </div>
      ) : !data ? (
        <p className="text-xs text-[#9CA3AF] text-center py-3">Could not load sales data.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatTile icon={DollarSign} label="Paid Revenue" count={data.paid.count} amount={data.paid.amount} tone="green" testId="popup-sales-paid" />
          <StatTile icon={Clock} label="Pending" count={data.pending.count} amount={data.pending.amount} tone="amber" testId="popup-sales-pending" />
          <StatTile icon={XCircle} label="Failed" count={data.failed.count} amount={data.failed.amount} tone="red" testId="popup-sales-failed" />
          <StatTile icon={RefreshCw} label="Refunded" count={data.refunded.count} amount={data.refunded.amount} tone="gray" testId="popup-sales-refunded" />
          <StatTile icon={ShoppingBag} label="Expired" count={data.expired.count} amount={data.expired.amount} tone="gray" testId="popup-sales-expired" />
        </div>
      )}
    </div>
  );
};

export default PopupShopSalesWidget;
